import { cn } from '../../lib/cn'

/**
 * The round-media player's glyphs, drawn inline in `currentColor`.
 *
 * The club carries no icon set (docs/fireside/golf-design-system.md §4), so
 * these are small hand-drawn SVGs transposed from the Caddie `round-media`
 * card. Every one is decorative — the button around it owns the accessible
 * name — so each is `aria-hidden` and kept out of the legacy tab order with
 * `focusable="false"`.
 */
interface MarkProps {
  className?: string
}

function Mark({
  viewBox,
  className,
  children,
  stroke = false,
}: MarkProps & { viewBox: string; children: React.ReactNode; stroke?: boolean }) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox={viewBox}
      className={cn('shrink-0', className ?? 'h-4 w-4')}
      fill={stroke ? 'none' : 'currentColor'}
      stroke={stroke ? 'currentColor' : undefined}
      strokeWidth={stroke ? 1.6 : undefined}
      strokeLinecap={stroke ? 'round' : undefined}
      strokeLinejoin={stroke ? 'round' : undefined}
    >
      {children}
    </svg>
  )
}

export function PlayMark({ className }: MarkProps) {
  return (
    <Mark viewBox="0 0 18 20" className={className}>
      <path d="M2 1.5v17L16 10z" />
    </Mark>
  )
}

export function PauseMark({ className }: MarkProps) {
  return (
    <Mark viewBox="0 0 14 16" className={className}>
      <rect x="1" width="4" height="16" rx="1" />
      <rect x="9" width="4" height="16" rx="1" />
    </Mark>
  )
}

export function ReplayMark({ className }: MarkProps) {
  return (
    <Mark viewBox="0 0 19 19" className={className} stroke>
      <path d="M3.2 7.2A7 7 0 1 1 3 12" />
      <path d="M3 3v4.5h4.5" />
    </Mark>
  )
}

/** A speaker: crossed out while muted, with its waves once sound is on. */
export function SoundMark({ muted, className }: MarkProps & { muted: boolean }) {
  return (
    <Mark viewBox="0 0 17 16" className={className} stroke>
      <path d="M2 6h3l4-3v10l-4-3H2z" />
      {muted ? (
        <path d="m12 6 4 4m0-4-4 4" />
      ) : (
        <>
          <path d="M11.5 5.5a3.5 3.5 0 0 1 0 5" />
          <path d="M13.8 3.2a6.5 6.5 0 0 1 0 9.6" />
        </>
      )}
    </Mark>
  )
}

export function FullscreenMark({ className }: MarkProps) {
  return (
    <Mark viewBox="0 0 15 15" className={className} stroke>
      <path d="M1 5V1h4M10 1h4v4M14 10v4h-4M5 14H1v-4" />
    </Mark>
  )
}

export function ExitFullscreenMark({ className }: MarkProps) {
  return (
    <Mark viewBox="0 0 15 15" className={className} stroke>
      <path d="M5 1H1v4M14 5V1h-4M10 14h4v-4M1 10v4h4" />
    </Mark>
  )
}
